import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { createAuditTask, unwrapPost } from "../../../../helpers/auditHub_helper";
import DueDateField from "../../AuditHub/DueDateField";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
} from "../../AuditHub/theme";

const TITLE_MAX = 200;
const DESCRIPTION_MAX = 4000;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\x00-\x1f\x7f-\x9f]/;

const EMPTY_FORM = { title: "", assignee: null, dueDate: "", description: "" };

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem", color: AH_MUTED },
  "& .MuiInputLabel-root.Mui-focused": { color: AH_TEXT },
  "& .MuiInputBase-input": { fontSize: "0.875rem", color: AH_TEXT },
  "& .MuiFormHelperText-root": { mx: 0.25, fontSize: 11.5 },
};

const closeXSx = {
  p: 0,
  m: 0,
  border: "none",
  background: "transparent",
  color: AH_WHITE,
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  WebkitTextStroke: `0.6px ${AH_WHITE}`,
  "&:disabled": { cursor: "not-allowed", opacity: 0.6 },
};

const buttonBaseSx = {
  textTransform: "none",
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  flexShrink: 0,
  boxShadow: "none",
};

const greyButtonSx = {
  ...buttonBaseSx,
  fontWeight: 600,
  color: AH_WHITE,
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  "&:hover": { backgroundColor: "#5a6268", borderColor: "#5a6268", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: AH_INPUT_BORDER, color: "#94a3b8", borderColor: AH_INPUT_BORDER },
};

const primaryButtonSx = {
  ...buttonBaseSx,
  fontWeight: 700,
  color: AH_WHITE,
  backgroundColor: AH_ACCENT,
  "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#fed7aa", color: AH_WHITE },
};

const CreateTaskDialog = ({ open, onClose, onCreated, auditId, auditControlId, controlCode, userOptions, auditDue }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setError(null);
      setSaving(false);
    }
  }, [open]);

  const title = form.title.trim();
  const titleInvalid = CONTROL_CHARS.test(title);
  const canSubmit = Boolean(title) && !titleInvalid && Boolean(form.assignee?.email) && !saving;

  const update = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    if (error) setError(null);
  };

  const close = () => {
    if (saving) return;
    onClose();
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    const description = form.description.trim();
    try {
      const task = unwrapPost(
        await createAuditTask(auditId, auditControlId, {
          title,
          assigneeEmail: form.assignee.email,
          description: description || null,
          dueDate: form.dueDate || null,
        })
      );
      setSaving(false);
      onCreated(task);
    } catch (err) {
      setError(err?.message || "Unable to create this task.");
      setSaving(false);
    }
  };

  const fieldId = (name) => `create-task-${name}-${auditControlId}`;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      aria-labelledby={fieldId("title-heading")}
      PaperProps={{
        component: "form",
        noValidate: true,
        onSubmit: (event) => {
          event.preventDefault();
          submit();
        },
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "none",
          border: `1px solid ${AH_INPUT_BORDER}`,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id={fieldId("title-heading")}
            sx={{ fontWeight: 700, fontSize: { xs: "1.125rem", sm: "1.25rem" }, color: AH_WHITE, letterSpacing: "-0.02em", lineHeight: 1.2 }}
          >
            New Task
          </Typography>
          <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem" }}>
            Assign a follow-up action for {controlCode || "this control"}.
          </Typography>
        </Box>
        <Box component="button" type="button" aria-label="Close" onClick={close} disabled={saving} sx={closeXSx}>
          <span aria-hidden="true">×</span>
        </Box>
      </Box>

      <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: AH_TRACK }}>
        <Box
          sx={{
            backgroundColor: AH_WHITE,
            border: `1px solid ${AH_INPUT_BORDER}`,
            borderRadius: "12px",
            p: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          <TextField
            id={fieldId("title")}
            label="Title"
            required
            autoFocus
            fullWidth
            size="small"
            value={form.title}
            disabled={saving}
            onChange={(event) => update({ title: event.target.value })}
            error={titleInvalid}
            helperText={titleInvalid ? "Title cannot contain control characters." : `${form.title.length}/${TITLE_MAX}`}
            inputProps={{ maxLength: TITLE_MAX }}
            sx={{ ...fieldSx, gridColumn: "1 / -1" }}
          />
          <Autocomplete
            id={fieldId("assignee")}
            size="small"
            options={userOptions}
            value={form.assignee}
            disabled={saving}
            onChange={(_event, option) => update({ assignee: option || null })}
            getOptionLabel={(option) => option.label || option.email || ""}
            isOptionEqualToValue={(a, b) => a.email === b.email}
            renderInput={(params) => <TextField {...params} label="Assignee" required sx={fieldSx} />}
          />
          <DueDateField
            id={fieldId("due-date")}
            value={form.dueDate}
            onChange={(dueDate) => update({ dueDate })}
            maxDate={auditDue}
            markedDate={auditDue}
            disabled={saving}
            fullWidth
            size="small"
          />
          <TextField
            id={fieldId("description")}
            label="Description"
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            size="small"
            value={form.description}
            disabled={saving}
            onChange={(event) => update({ description: event.target.value })}
            helperText={`${form.description.length}/${DESCRIPTION_MAX}`}
            inputProps={{ maxLength: DESCRIPTION_MAX }}
            sx={{ ...fieldSx, gridColumn: "1 / -1" }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{
          px: 2.5,
          py: 1.5,
          gap: 1.5,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          borderTop: `1px solid ${AH_INPUT_BORDER}`,
          bgcolor: AH_WHITE,
          justifyContent: "space-between",
        }}
      >
        <Button type="button" variant="contained" disableElevation onClick={close} disabled={saving} sx={greyButtonSx}>
          Cancel
        </Button>
        {error ? (
          <Typography
            role="alert"
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "#dc2626",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={!canSubmit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={primaryButtonSx}
        >
          {saving ? "Creating..." : "Create Task"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTaskDialog;
